import React, { useCallback, useEffect, useState } from "react";
import { Table, Typography, Switch, message, Button, Space, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { AdminNavBar } from "../components/AdminNavBar";
import { trackFeatureUsage } from "../lib/posthog";
import {
  getAllShippingMethodSettings,
  setShippingMethodEnabled,
  type SendcloudShippingMethodSetting,
} from "../services/sendcloudShippingMethodSettingsService";

const { Title, Paragraph } = Typography;

const SendcloudShippingMethodsAdmin: React.FC = () => {
  const [settings, setSettings] = useState<SendcloudShippingMethodSetting[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllShippingMethodSettings();
      setSettings(data);
    } catch (error) {
      console.error("Error loading shipping method settings:", error);
      message.error("Failed to load shipping methods");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    trackFeatureUsage("admin_shipping_methods");
    loadSettings();
  }, [loadSettings]);

  const handleToggle = async (
    shippingMethodId: number,
    enabled: boolean,
  ) => {
    setTogglingId(shippingMethodId);
    try {
      await setShippingMethodEnabled(shippingMethodId, enabled);
      setSettings((prev) =>
        prev.map((row) =>
          row.shipping_method_id === shippingMethodId
            ? {
                ...row,
                is_enabled: enabled,
                updated_at: new Date().toISOString(),
              }
            : row,
        ),
      );
      message.success(
        enabled ? "Shipping method enabled" : "Shipping method disabled",
      );
    } catch (error) {
      console.error("Error updating shipping method:", error);
      message.error("Failed to update shipping method");
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: SendcloudShippingMethodSetting) => (
        <Space>
          {record.carrier && (
            <img
              src={`https://cdn.sendcloud.com/global-media/${record.carrier.toLowerCase()}/img/logo.svg`}
              alt={record.carrier}
              style={{
                height: 18,
                maxWidth: 48,
                objectFit: "contain",
                display: "block",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
          <span>{name}</span>
        </Space>
      ),
    },
    {
      title: "Carrier",
      dataIndex: "carrier",
      key: "carrier",
      render: (carrier: string | null) =>
        carrier ? (
          <Tag>{carrier.toUpperCase()}</Tag>
        ) : (
          <span style={{ color: "rgba(0,0,0,0.45)" }}>—</span>
        ),
    },
    {
      title: "Method ID",
      dataIndex: "shipping_method_id",
      key: "shipping_method_id",
    },
    {
      title: "Enabled",
      dataIndex: "is_enabled",
      key: "is_enabled",
      render: (isEnabled: boolean, record: SendcloudShippingMethodSetting) => (
        <Switch
          checked={isEnabled}
          loading={togglingId === record.shipping_method_id}
          onChange={(checked) =>
            handleToggle(record.shipping_method_id, checked)
          }
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <AdminNavBar />
      <Title level={2}>Sendcloud Shipping Methods</Title>
      <Paragraph type="secondary">
        Enable or disable shipping methods offered when creating labels. Method
        3366 is only shown for Belgium destinations even when enabled.
      </Paragraph>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ReloadOutlined />}
          onClick={loadSettings}
          loading={loading}
        >
          Refresh
        </Button>
      </Space>
      <Table
        rowKey="shipping_method_id"
        columns={columns}
        dataSource={settings}
        loading={loading}
        pagination={false}
      />
    </div>
  );
};

export default SendcloudShippingMethodsAdmin;
